import { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { Plus, Activity, AlertTriangle } from 'lucide-react';
import { getVitals, addVital, type IpdVital, type AddVitalPayload } from '@/lib/ipdApi';
import { format } from 'date-fns';

// ─── Abnormal value helpers ───────────────────────────────────────────────────

function bpClass(sys: number | null, dia: number | null): string {
  if (sys != null && sys < 90) return 'text-destructive font-semibold';
  if (sys != null && sys > 140) return 'text-warning font-medium';
  if (dia != null && dia > 90)  return 'text-warning font-medium';
  return '';
}
function spo2Class(v: number | null): string {
  if (v != null && v < 90) return 'text-destructive font-semibold';
  if (v != null && v < 95) return 'text-warning font-medium';
  return '';
}
function tempClass(v: number | null): string {
  if (v != null && (v >= 38 || v < 35)) return 'text-warning font-medium';
  return '';
}
function rrClass(v: number | null): string {
  if (v != null && (v > 24 || v < 10)) return 'text-warning font-medium';
  return '';
}

// Readings outside these can't be real — a typo or the wrong unit (same limits as the server)
const LIMITS: [keyof AddVitalPayload, string, number, number][] = [
  ['bp_systolic',      'Systolic BP',      40,  300],
  ['bp_diastolic',     'Diastolic BP',     20,  200],
  ['pulse',            'Pulse',            20,  250],
  ['temp_celsius',     'Temperature',      30,  45],
  ['spo2',             'SpO2',             30,  100],
  ['respiratory_rate', 'Respiratory rate', 4,   80],
  ['weight_kg',        'Weight',           0.3, 400],
  ['urine_output_ml',  'Urine output',     0,   20000],
  ['fluid_intake_ml',  'Fluid intake',     0,   20000],
];

/** Temperature is entered and shown in °F like the rest of the app, and stored in °C.
 *  A value under 50 can only be °C, so it's taken as typed. */
function tempToCelsius(s: string): number | null {
  const v = numOrNull(s);
  if (v == null) return null;
  return v < 50 ? v : Math.round(((v - 32) * 5 / 9) * 100) / 100;
}
const toF = (c: number) => Math.round((c * 9 / 5 + 32) * 10) / 10;
function pulseClass(v: number | null): string {
  if (v != null && (v > 100 || v < 60)) return 'text-warning font-medium';
  return '';
}

function fmtBP(sys: number | null, dia: number | null): string {
  if (sys == null && dia == null) return '—';
  return `${sys ?? '?'}/${dia ?? '?'}`;
}
function fmtVal(v: number | null, unit = ''): string {
  if (v == null) return '—';
  return `${v}${unit}`;
}

// ─── Vitals Form ──────────────────────────────────────────────────────────────

interface VitalsFormState {
  recorded_at: string;
  bp_systolic: string;
  bp_diastolic: string;
  pulse: string;
  temp_celsius: string;
  spo2: string;
  respiratory_rate: string;
  weight_kg: string;
  urine_output_ml: string;
  fluid_intake_ml: string;
  notes: string;
}

const emptyForm = (): VitalsFormState => ({
  recorded_at: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
  bp_systolic: '', bp_diastolic: '',
  pulse: '', temp_celsius: '', spo2: '',
  respiratory_rate: '', weight_kg: '',
  urine_output_ml: '', fluid_intake_ml: '',
  notes: '',
});

function numOrNull(s: string): number | null {
  const n = parseFloat(s);
  return s.trim() === '' || isNaN(n) ? null : n;
}

interface FieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  unit?: string;
  step?: string;
  placeholder?: string;
}

function VitalField({ label, value, onChange, unit, step = 'any', placeholder }: FieldProps) {
  return (
    <div>
      <label className="block text-xs font-medium text-muted-foreground mb-1">
        {label}{unit && <span className="text-muted-foreground/60"> ({unit})</span>}
      </label>
      <Input
        type="number"
        min="0"
        step={step}
        value={value}
        placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
        className="h-8 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
      />
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

interface Props {
  admissionId: string;
  isActive?: boolean;
  admissionDate?: string | null;
  dischargeDate?: string | null;
}

export default function IPDVitalsTab({ admissionId, isActive = true, admissionDate, dischargeDate }: Props) {
  const minRecordedAt = admissionDate ? format(new Date(admissionDate), "yyyy-MM-dd'T'HH:mm") : undefined;
  // For active admissions maxRecordedAt is "now" — recomputed per-render so the
  // picker stays current as the user fills the form. For discharged admissions
  // it's fixed to the discharge timestamp.
  const maxRecordedAt = format(dischargeDate ? new Date(dischargeDate) : new Date(), "yyyy-MM-dd'T'HH:mm");
  const [vitals, setVitals]   = useState<IpdVital[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen]       = useState(false);
  const [form, setForm]       = useState<VitalsFormState>(emptyForm());
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getVitals(admissionId);
      setVitals(data);
    } catch (e: any) {
      console.error('Failed to load vitals:', e);
    } finally {
      setLoading(false);
    }
  }, [admissionId]);

  useEffect(() => { load(); }, [load]);

  const bpDiaRef = useRef<HTMLInputElement>(null);

  const set = (key: keyof VitalsFormState) => (v: string) =>
    setForm(f => ({ ...f, [key]: v }));

  const handleSave = async () => {
    setError('');

    // Validate recorded_at is within the admission window.
    // This catches manual keyboard input that bypasses the HTML min/max attributes.
    const nowStr = format(new Date(), "yyyy-MM-dd'T'HH:mm");
    const effectiveMax = dischargeDate
      ? format(new Date(dischargeDate), "yyyy-MM-dd'T'HH:mm")
      : nowStr;

    if (minRecordedAt && form.recorded_at < minRecordedAt) {
      setError('Recorded time cannot be before the admission date.');
      return;
    }
    if (form.recorded_at > effectiveMax) {
      setError(dischargeDate
        ? 'Recorded time cannot be after the discharge date.'
        : 'Recorded time cannot be in the future.');
      return;
    }

    const payload: AddVitalPayload = {
      recorded_at:     form.recorded_at ? new Date(form.recorded_at).toISOString() : undefined,
      bp_systolic:     numOrNull(form.bp_systolic),
      bp_diastolic:    numOrNull(form.bp_diastolic),
      pulse:           numOrNull(form.pulse),
      temp_celsius:    tempToCelsius(form.temp_celsius),
      spo2:            numOrNull(form.spo2),
      respiratory_rate: numOrNull(form.respiratory_rate),
      weight_kg:       numOrNull(form.weight_kg),
      urine_output_ml: numOrNull(form.urine_output_ml),
      fluid_intake_ml: numOrNull(form.fluid_intake_ml),
      notes:           form.notes.trim() || undefined,
    };

    // At least one clinical value
    const clinical = [
      payload.bp_systolic, payload.bp_diastolic, payload.pulse,
      payload.temp_celsius, payload.spo2, payload.respiratory_rate,
      payload.weight_kg, payload.urine_output_ml, payload.fluid_intake_ml,
    ];
    if (clinical.every(v => v == null)) {
      setError('Enter at least one vital value.');
      return;
    }
    for (const [key, label, min, max] of LIMITS) {
      const v = payload[key] as number | null | undefined;
      if (v != null && (v < min || v > max)) {
        setError(key === 'temp_celsius'
          ? 'Temperature looks wrong — enter it in °F, e.g. 98.6.'
          : `${label} must be between ${min} and ${max}.`);
        return;
      }
    }
    if ((payload.bp_systolic == null) !== (payload.bp_diastolic == null)) {
      setError('Enter both systolic and diastolic BP.');
      return;
    }
    if (payload.bp_systolic != null && payload.bp_diastolic! >= payload.bp_systolic) {
      setError('Diastolic BP must be lower than systolic.');
      return;
    }

    try {
      setSaving(true);
      await addVital(admissionId, payload);
      setOpen(false);
      setForm(emptyForm());
      await load();
    } catch (e: any) {
      setError(e.message || 'Failed to save vitals.');
    } finally {
      setSaving(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Activity className="w-4 h-4" />
          <span>{vitals.length} {vitals.length === 1 ? 'entry' : 'entries'}</span>
        </div>
        {isActive && (
          <Button size="sm" onClick={() => { setForm(emptyForm()); setError(''); setOpen(true); }}>
            <Plus className="w-4 h-4 mr-1" /> Record vitals
          </Button>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>
      ) : vitals.length === 0 ? (
        <div className="text-sm text-muted-foreground py-12 text-center">
          No vitals recorded yet.
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="h-8 border-b border-border bg-muted/40">
                {['Time', 'BP', 'Pulse', 'Temp', 'SpO2', 'RR', 'Weight', 'Urine', 'Fluid in', 'By'].map(h => (
                  <th key={h} scope="col" className={cn('px-3 text-left text-2xs font-medium uppercase tracking-wide text-muted-foreground whitespace-nowrap', h !== 'Time' && h !== 'By' && 'text-right')}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {vitals.map((v) => (
                <tr key={v.id} className="h-10 border-b border-divider last:border-0">
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-muted-foreground tabular-nums">
                    {format(new Date(v.recorded_at), 'dd MMM, h:mm a')}
                  </td>
                  <td className={cn('px-3 py-2 whitespace-nowrap text-right font-mono tabular-nums', bpClass(v.bp_systolic, v.bp_diastolic))}>
                    {fmtBP(v.bp_systolic, v.bp_diastolic)}
                  </td>
                  <td className={cn('px-3 py-2 whitespace-nowrap text-right font-mono tabular-nums', pulseClass(v.pulse))}>
                    {fmtVal(v.pulse)}
                  </td>
                  <td className={cn('px-3 py-2 whitespace-nowrap text-right font-mono tabular-nums', tempClass(v.temp_celsius))}>
                    {v.temp_celsius == null ? '—' : `${toF(Number(v.temp_celsius))}°F`}
                  </td>
                  <td className={cn('px-3 py-2 whitespace-nowrap text-right font-mono tabular-nums', spo2Class(v.spo2))}>
                    {fmtVal(v.spo2, '%')}
                  </td>
                  <td className={cn('px-3 py-2 whitespace-nowrap text-right font-mono tabular-nums', rrClass(v.respiratory_rate))}>{fmtVal(v.respiratory_rate)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right font-mono tabular-nums">{fmtVal(v.weight_kg, ' kg')}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right font-mono tabular-nums">{fmtVal(v.urine_output_ml, ' ml')}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-right font-mono tabular-nums">{fmtVal(v.fluid_intake_ml, ' ml')}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-muted-foreground">
                    {v.recorded_by_name}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Record Vitals Modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Record vitals</DialogTitle>
          </DialogHeader>

          {/* A form, so Enter in any box saves */}
          <form id="ipd-vitals-form" className="space-y-4 py-2" onSubmit={e => { e.preventDefault(); handleSave(); }}>
            {/* Date/time */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Recorded at</label>
              <Input
                type="datetime-local"
                value={form.recorded_at}
                min={minRecordedAt}
                max={maxRecordedAt}
                onChange={e => set('recorded_at')(e.target.value)}
                className="h-8 text-sm"
              />
            </div>

            {/* BP — side by side */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">
                Blood pressure <span className="text-muted-foreground/60">(mmHg)</span>
              </label>
              <div className="flex items-center gap-1">
                <Input
                  type="number" min="0" step="1"
                  placeholder="Sys"
                  autoFocus
                  value={form.bp_systolic}
                  onChange={e => set('bp_systolic')(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === '/' || e.key === ' ') { e.preventDefault(); bpDiaRef.current?.focus(); }
                  }}
                  className="h-8 w-20 text-sm text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <span className="text-muted-foreground text-sm">/</span>
                <Input
                  type="number" min="0" step="1"
                  placeholder="Dia"
                  ref={bpDiaRef}
                  value={form.bp_diastolic}
                  onChange={e => set('bp_diastolic')(e.target.value)}
                  className="h-8 w-20 text-sm text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </div>
            </div>

            {/* 2-column grid for the rest */}
            <div className="grid grid-cols-2 gap-3">
              <VitalField label="Pulse"            unit="bpm"  value={form.pulse}            onChange={set('pulse')}            step="1"   />
              <div>
                <VitalField label="Temperature" unit="°F" value={form.temp_celsius} onChange={set('temp_celsius')} step="0.1" />
                {numOrNull(form.temp_celsius) != null && numOrNull(form.temp_celsius)! < 50 && (
                  <p className="mt-1 text-xs text-muted-foreground tabular-nums">Read as °C = {toF(numOrNull(form.temp_celsius)!)} °F</p>
                )}
              </div>
              <VitalField label="SpO2"             unit="%"    value={form.spo2}              onChange={set('spo2')}             step="1"   />
              <VitalField label="Respiratory rate" unit="/min" value={form.respiratory_rate}  onChange={set('respiratory_rate')} step="1"   />
              <VitalField label="Weight"           unit="kg"   value={form.weight_kg}         onChange={set('weight_kg')}        step="0.1" />
              <VitalField label="Urine output"     unit="ml"   value={form.urine_output_ml}   onChange={set('urine_output_ml')}  step="1"   />
              <VitalField label="Fluid intake"     unit="ml"   value={form.fluid_intake_ml}   onChange={set('fluid_intake_ml')}  step="1"   />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Notes</label>
              <Input
                value={form.notes}
                onChange={e => set('notes')(e.target.value)}
                placeholder="Optional"
                className="h-8 text-sm"
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded px-3 py-2">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                {error}
              </div>
            )}
          </form>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button type="submit" form="ipd-vitals-form" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
